import type { ComponentType, SVGProps } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

export type NotificationSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  severity: NotificationSeverity;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
  action_url?: string;
  action_label?: string;
}

export type NotificationIcon = ComponentType<SVGProps<SVGSVGElement>>;

export const getNotificationIcon = (severity: NotificationSeverity): NotificationIcon => {
  switch (severity) {
    case 'critical':
    case 'high':
      return ExclamationTriangleIcon;
    case 'medium':
      return InformationCircleIcon;
    case 'low':
    default:
      return CheckCircleIcon;
  }
};

export const getNotificationColor = (severity: NotificationSeverity): string => {
  switch (severity) {
    case 'critical':
      return 'text-red-600 dark:text-red-400';
    case 'high':
      return 'text-orange-600 dark:text-orange-400';
    case 'medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'low':
    default:
      return 'text-blue-600 dark:text-blue-400';
  }
};

export const formatNotificationTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
};

export const getSafeInternalPath = (
  rawUrl: string,
  origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
): string | null => {
  try {
    const parsed = new URL(rawUrl, origin);
    const isSameOrigin = parsed.origin === origin;
    const isHttpProtocol = parsed.protocol === 'http:' || parsed.protocol === 'https:';

    if (!isSameOrigin || !isHttpProtocol) {
      return null;
    }

    if (!parsed.pathname.startsWith('/')) {
      return null;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};
